import {
  CHARACTER_NAME_MAX_LENGTH,
  CharacterNameSchema,
  type Player,
  type PlayerId,
} from '@webgame/shared';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { isApiError } from '../../core/api/api-client';
import type { CharacterList } from '../../core/session/session-client';
import { LanguageSwitcher } from '../components/language-switcher';
import { useTranslator } from '../hooks/use-translator';
import { useBoot } from '../services';
import { PANEL_THEME } from '../game/panel-theme';
import { firstScreenShown } from './game-screen-loader';

type LoadState =
  { kind: 'loading' } | { kind: 'loaded'; list: CharacterList } | { kind: 'failed'; code: string };

const errorCode = (error: unknown): string => (isApiError(error) ? error.code : 'unknown');

/** The signed-in account's characters (ADR 0029): play one or create a new one. */
export function CharacterSelectScreen() {
  const { session: client } = useBoot();
  const translator = useTranslator();
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [pending, setPending] = useState<PlayerId | 'create' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  // CHAR-ORIGIN: where the new character comes from; the first offered until one is chosen.
  const [origin, setOrigin] = useState<string | null>(null);
  // B165, PQ-6: skipping the prologue's memory, offered once the account lived one.
  const [skipMemory, setSkipMemory] = useState(false);

  const fetchCharacters = useCallback(() => {
    setLoad({ kind: 'loading' });
    client.listCharacters().then(
      (list) => {
        setLoad({ kind: 'loaded', list });
        firstScreenShown();
      },
      (failure: unknown) => {
        setLoad({ kind: 'failed', code: errorCode(failure) });
        firstScreenShown();
      },
    );
  }, [client]);

  useEffect(fetchCharacters, [fetchCharacters]);

  const play = (playerId: PlayerId) => {
    setPending(playerId);
    setError(null);
    client.play(playerId).catch((failure: unknown) => {
      setPending(null);
      setError(errorCode(failure));
    });
  };

  // The server validates the name; checking here only spares a request that would fail.
  const nameIsValid = CharacterNameSchema.safeParse(name).success;
  const create = () => {
    setPending('create');
    setError(null);
    const offered = load.kind === 'loaded' ? load.list.origins : [];
    const from = origin !== null && offered.includes(origin) ? origin : offered[0];
    const skip = skipMemory && load.kind === 'loaded' && load.list.canSkipMemory;
    client.createCharacter(name, from, skip).then(
      () => {
        setPending(null);
        setName('');
        setSkipMemory(false);
        fetchCharacters();
      },
      (failure: unknown) => {
        setPending(null);
        setError(errorCode(failure));
      },
    );
  };

  return (
    <main class="screen screen--centered" data-testid="character-select">
      <header class="screen__toolbar">
        <LanguageSwitcher />
      </header>
      <section class="panel panel--narrow" data-theme={PANEL_THEME}>
        <h1 class="panel__title">{translator.t('screens.characters.title')}</h1>

        {error && (
          <p class="notice notice--error" role="alert">
            {translator.error(error)}
          </p>
        )}

        {load.kind === 'loading' && <p class="muted">{translator.t('common.loading')}</p>}

        {load.kind === 'failed' && (
          <div class="notice notice--error" role="alert">
            <p>{translator.t('screens.characters.loadFailed')}</p>
            <p class="muted">{translator.error(load.code)}</p>
            <button type="button" class="button" onClick={fetchCharacters}>
              {translator.t('common.retry')}
            </button>
          </div>
        )}

        {load.kind === 'loaded' && (
          <>
            <p class="panel__subtitle" data-testid="character-count">
              {translator.t('screens.characters.count', {
                count: translator.formatNumber(load.list.characters.length),
                limit: translator.formatNumber(load.list.limit),
              })}
            </p>
            {load.list.characters.length === 0 ? (
              <p class="notice">{translator.t('screens.characters.empty')}</p>
            ) : (
              <ul class="player-list">
                {load.list.characters.map((character) => (
                  <CharacterCard
                    key={character.id}
                    character={character}
                    serverTime={load.list.serverTime}
                    pending={pending}
                    onPlay={() => {
                      play(character.id);
                    }}
                    onChanged={fetchCharacters}
                    onError={setError}
                  />
                ))}
              </ul>
            )}

            <section class="panel__section" aria-labelledby="new-character">
              <h2 class="panel__section-title" id="new-character">
                {translator.t('screens.characters.createTitle')}
              </h2>
              {load.list.characters.length >= load.list.limit ? (
                <p class="notice">
                  {translator.t('screens.characters.limitReached', {
                    limit: translator.formatNumber(load.list.limit),
                  })}
                </p>
              ) : (
                <form
                  class="field"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (nameIsValid) create();
                  }}
                >
                  {load.list.origins.length > 0 && (
                    <OriginChoice
                      origins={load.list.origins}
                      chosen={
                        origin !== null && load.list.origins.includes(origin)
                          ? origin
                          : (load.list.origins[0] ?? '')
                      }
                      disabled={pending !== null}
                      onChoose={setOrigin}
                    />
                  )}
                  {load.list.canSkipMemory && (
                    <label class="field__check" data-testid="skip-memory">
                      <input
                        type="checkbox"
                        data-testid="skip-memory-toggle"
                        checked={skipMemory}
                        disabled={pending !== null}
                        onChange={(event) => {
                          setSkipMemory(event.currentTarget.checked);
                        }}
                      />
                      <span>{translator.t('screens.characters.skipMemory')}</span>
                      <span class="muted">{translator.t('screens.characters.skipMemoryHint')}</span>
                    </label>
                  )}
                  <label class="field__label" for="character-name">
                    {translator.t('screens.characters.nameLabel')}
                  </label>
                  <div class="form-row">
                    <input
                      id="character-name"
                      data-testid="character-name"
                      maxLength={CHARACTER_NAME_MAX_LENGTH * 2}
                      autoComplete="off"
                      value={name}
                      onInput={(event) => {
                        setName(event.currentTarget.value);
                      }}
                    />
                    <button
                      type="submit"
                      class="button"
                      data-testid="character-create"
                      disabled={pending !== null || !nameIsValid}
                      aria-busy={pending === 'create'}
                    >
                      {translator.t('screens.characters.create')}
                    </button>
                  </div>
                  <span class="field__hint">{translator.t('screens.characters.nameHint')}</span>
                </form>
              )}
            </section>
          </>
        )}

        <button
          type="button"
          class="button"
          disabled={pending !== null}
          onClick={() => {
            void client.signOut();
          }}
        >
          {translator.t('common.signOut')}
        </button>
      </section>
    </main>
  );
}

/**
 * Where a new character comes from (CHAR-ORIGIN phase 1): the origins the server offers, in its
 * order, each with its name and its hook from the catalogs (`origins.<id>`); one is always chosen.
 */
function OriginChoice(props: {
  readonly origins: readonly string[];
  readonly chosen: string;
  readonly disabled: boolean;
  readonly onChoose: (origin: string) => void;
}) {
  const translator = useTranslator();
  return (
    <fieldset class="origin-choice" data-testid="origin-choice">
      <legend class="field__label">{translator.t('screens.characters.originLabel')}</legend>
      {props.origins.map((id) => (
        <label
          key={id}
          class="origin-choice__card"
          data-testid={`origin-${id}`}
          data-chosen={id === props.chosen}
        >
          <input
            type="radio"
            name="origin"
            value={id}
            checked={id === props.chosen}
            disabled={props.disabled}
            onChange={() => {
              props.onChoose(id);
            }}
          />
          <span class="origin-choice__text">
            <strong>{translator.lookup(`origins.${id}.name`) ?? id}</strong>
            <span class="muted">{translator.lookup(`origins.${id}.hook`) ?? ''}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * One of the account's characters: played, or deleted (CHAR-DELETE, CD-1..8) once its name is
 * typed to confirm; one being deleted says when it goes and may be kept, and is not played.
 */
function CharacterCard(props: {
  readonly character: Player;
  /** Game time as the list was read: `deleteAt` is game time, so its day is told from it. */
  readonly serverTime: number | undefined;
  readonly pending: PlayerId | 'create' | null;
  readonly onPlay: () => void;
  readonly onChanged: () => void;
  readonly onError: (code: string) => void;
}) {
  const { session: client } = useBoot();
  const translator = useTranslator();
  const { character } = props;
  const name = character.displayName;
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const act = (call: Promise<unknown>) => {
    setBusy(true);
    call.then(
      () => {
        setBusy(false);
        setConfirming(false);
        setTyped('');
        props.onChanged();
      },
      (failure: unknown) => {
        setBusy(false);
        props.onError(errorCode(failure));
      },
    );
  };
  const deleteAt = character.deleteAt;
  return (
    <li
      class="player-card"
      data-testid={`character-${name}`}
      data-deleting={deleteAt !== undefined}
    >
      <span class="player-card__name">{name}</span>
      {deleteAt !== undefined ? (
        <>
          <span class="muted" data-testid={`deleting-${name}`}>
            {translator.t('screens.characters.deleting', {
              name,
              // What is left of its grace, from now: game time runs as real time but for the dev
              // clock's offset, which `serverTime` carries.
              date: new Date(
                props.serverTime === undefined
                  ? deleteAt
                  : Date.now() + (deleteAt - props.serverTime),
              ).toLocaleDateString(translator.locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }),
            })}
          </span>
          <button
            type="button"
            class="button"
            data-testid={`cancel-delete-${name}`}
            disabled={busy}
            onClick={() => {
              act(client.cancelDeleteCharacter(character.id));
            }}
          >
            {translator.t('screens.characters.cancelDelete')}
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            class="button button--primary"
            data-testid={`play-${name}`}
            aria-label={translator.t('screens.characters.playAs', { name })}
            disabled={props.pending !== null || busy}
            aria-busy={props.pending === character.id}
            onClick={props.onPlay}
          >
            {props.pending === character.id
              ? translator.t('common.pending')
              : translator.t('screens.characters.play')}
          </button>
          {!confirming && (
            <button
              type="button"
              class="button"
              data-testid={`delete-${name}`}
              aria-label={translator.t('screens.characters.deleteAs', { name })}
              disabled={props.pending !== null || busy}
              onClick={() => {
                setConfirming(true);
              }}
            >
              {translator.t('screens.characters.delete')}
            </button>
          )}
        </>
      )}
      {confirming && deleteAt === undefined && (
        <div class="player-card__confirm" data-testid={`delete-confirm-${name}`}>
          <p>{translator.t('screens.characters.deleteConfirm', { name })}</p>
          <label class="field">
            <span class="field__label">{translator.t('screens.characters.deleteNameLabel')}</span>
            <input
              type="text"
              data-testid="delete-confirm-name"
              autocomplete="off"
              value={typed}
              onInput={(event) => {
                setTyped(event.currentTarget.value);
              }}
            />
          </label>
          <div class="ui-actions">
            <button
              type="button"
              class="button button--danger"
              data-testid="delete-confirm-go"
              disabled={busy || typed.trim() !== name}
              onClick={() => {
                act(client.deleteCharacter(character.id));
              }}
            >
              {translator.t('screens.characters.deleteGo')}
            </button>
            <button
              type="button"
              class="button"
              data-testid="delete-confirm-keep"
              disabled={busy}
              onClick={() => {
                setConfirming(false);
                setTyped('');
              }}
            >
              {translator.t('screens.characters.deleteKeep')}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
